import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faInbox, faPaperPlane, faReply, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { getAuth } from 'firebase/auth';
import { addDoc, collection, limit as firestoreLimit, onSnapshot, orderBy, query, serverTimestamp } from 'firebase/firestore';
import './dash.css';
import { db } from '../firebase';
import { ORGANIZATION_NAME } from '../branding';
import { logAdminActivity } from '../utils/activityLogger';
import { buildMinimalEmail } from '../utils/emailTemplates';

const MAIL_API_BASE_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/mailApi';
const LEFTBAR_EMAIL_BADGE_KEY = 'leftbarEmailBadgeState';
const LEFTBAR_EMAIL_BADGE_EVENT = 'leftbar-email-badge-update';
const LOCAL_PENDING_SENT_EMAILS_KEY = 'emailsPendingSentCache';
const MAILBOX_LABELS = {
  INBOX: 'Inbox',
  sent: 'Sent',
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const dateObj = new Date(value);
  if (Number.isNaN(dateObj.getTime())) return '—';
  return `${dateObj.toLocaleDateString()} • ${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const parseResponseJson = async (response) => {
  const rawText = await response.text();
  let data = {};

  if (rawText) {
    try {
      data = JSON.parse(rawText);
    } catch (error) {
      data = { message: rawText };
    }
  }

  if (!response.ok) {
    throw new Error(data.error || data.message || `Request failed with status ${response.status}`);
  }

  return data;
};

const ensureReplySubject = (subject) => {
  const trimmed = String(subject || '').trim();
  if (!trimmed) return 'Re:';
  if (/^re:/i.test(trimmed)) return trimmed;
  return `Re: ${trimmed}`;
};

const extractEmailAddress = (value) => {
  const text = String(value || '').trim();
  const angleMatch = text.match(/<([^>]+)>/);
  if (angleMatch?.[1]) {
    return angleMatch[1].trim();
  }
  return text;
};

const textToHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/\n/g, '<br />');

const buildCommunicationEmailHtml = ({ subject, message }) => buildMinimalEmail({
  eyebrow: 'Communication',
  title: subject || `Message from ${ORGANIZATION_NAME}`,
  bodyHtml: `
    <div style="color:#667085;font-size:14px;line-height:1.8;">
      ${textToHtml(message || '')}
    </div>
  `
});

const buildLocalSentEmail = ({ to, cc, subject, text, html, messageId }) => ({
  uid: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  mailbox: 'sent',
  messageId: messageId || '',
  inReplyTo: '',
  references: [],
  subject: subject || '(No subject)',
  from: ORGANIZATION_NAME,
  to: to || '',
  cc: cc || '',
  bcc: '',
  date: new Date().toISOString(),
  text: text || '',
  html: html || textToHtml(text || ''),
  snippet: String(text || '').slice(0, 220),
  seen: true,
  isLocalOnly: true,
});

const buildSharedSentEmail = (docId, data = {}) => ({
  uid: `shared-${docId}`,
  mailbox: 'sent',
  messageId: data.messageId || '',
  inReplyTo: data.inReplyTo || '',
  references: Array.isArray(data.references) ? data.references : [],
  subject: data.subject || '(No subject)',
  from: data.fromLabel || data.fromEmail || ORGANIZATION_NAME,
  to: data.to || '',
  cc: data.cc || '',
  bcc: data.bcc || '',
  date: data.sentAt?.toDate?.()?.toISOString?.() || data.createdAt?.toDate?.()?.toISOString?.() || new Date().toISOString(),
  text: data.text || '',
  html: data.html || textToHtml(data.text || ''),
  snippet: String(data.snippet || data.text || '').slice(0, 220),
  seen: true,
  sharedDocId: docId,
  isSharedCopy: true,
  sentByName: data.sentByName || '',
  sentByEmail: data.sentByEmail || '',
});

const readStoredPendingSentEmails = () => {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(LOCAL_PENDING_SENT_EMAILS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((email) => email && typeof email === 'object')
      .map((email) => ({
        ...email,
        mailbox: 'sent',
        isLocalOnly: true,
      }));
  } catch (error) {
    console.error('Failed to read cached sent emails:', error);
    return [];
  }
};

const storePendingSentEmails = (emails) => {
  if (typeof window === 'undefined') return;

  const pendingEmails = Array.isArray(emails)
    ? emails.filter((email) => email?.isLocalOnly)
    : [];

  if (pendingEmails.length === 0) {
    window.localStorage.removeItem(LOCAL_PENDING_SENT_EMAILS_KEY);
    return;
  }

  window.localStorage.setItem(LOCAL_PENDING_SENT_EMAILS_KEY, JSON.stringify(pendingEmails));
};

const getEmailIdentity = (email) => {
  const messageId = String(email?.messageId || '').trim();
  if (messageId) return `message:${messageId}`;

  const uid = String(email?.uid || '').trim();
  if (uid && !uid.startsWith('local-')) return `uid:${uid}`;

  return [
    String(email?.to || '').trim().toLowerCase(),
    String(email?.subject || '').trim().toLowerCase(),
    String(email?.date || '').trim(),
    String(email?.text || '').trim().slice(0, 120),
  ].join('|');
};

const areEmailListsEqual = (firstList, secondList) => {
  if (firstList === secondList) return true;
  if (!Array.isArray(firstList) || !Array.isArray(secondList)) return false;
  if (firstList.length !== secondList.length) return false;

  return firstList.every((email, index) => getEmailIdentity(email) === getEmailIdentity(secondList[index]));
};

const mergeSentEmails = (remoteEmails, sharedEmails, pendingEmails) => {
  const normalizedRemote = Array.isArray(remoteEmails) ? remoteEmails : [];
  const normalizedShared = Array.isArray(sharedEmails) ? sharedEmails : [];
  const normalizedPending = Array.isArray(pendingEmails) ? pendingEmails : [];
  const sourceKeys = new Set([
    ...normalizedRemote.map((email) => getEmailIdentity(email)),
    ...normalizedShared.map((email) => getEmailIdentity(email)),
  ]);

  const remainingPendingEmails = normalizedPending.filter((email) => !sourceKeys.has(getEmailIdentity(email)));
  const dedupedEmails = [];
  const seenKeys = new Set();

  [...normalizedRemote, ...normalizedShared, ...remainingPendingEmails].forEach((email) => {
    const key = getEmailIdentity(email);
    if (!key || seenKeys.has(key)) return;
    seenKeys.add(key);
    dedupedEmails.push(email);
  });

  const mergedEmails = dedupedEmails
    .sort((first, second) => new Date(second?.date || 0) - new Date(first?.date || 0));

  return { mergedEmails, remainingPendingEmails };
};

const getSentEmailStatus = (email) => {
  if (email?.isLocalOnly) {
    return { label: 'Pending sync', tone: 'pending' };
  }

  if (email?.isSharedCopy) {
    return { label: 'Shared', tone: 'shared' };
  }

  return { label: 'Mailbox', tone: 'mailbox' };
};

const persistSharedSentEmail = async ({ to, cc, bcc, subject, text, html, messageId, inReplyTo, references }) => {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const sentByName = currentUser?.displayName || currentUser?.email || 'Admin';
  const fromEmail = currentUser?.email || '';

  await addDoc(collection(db, 'SharedSentEmails'), {
    to: to || '',
    cc: cc || '',
    bcc: bcc || '',
    subject: subject || '',
    text: text || '',
    html: html || textToHtml(text || ''),
    snippet: String(text || '').slice(0, 220),
    messageId: messageId || '',
    inReplyTo: inReplyTo || '',
    references: Array.isArray(references) ? references : [],
    fromEmail,
    fromLabel: `${ORGANIZATION_NAME} <${fromEmail}>`,
    sentById: currentUser?.uid || '',
    sentByEmail: currentUser?.email || '',
    sentByName,
    sentAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  });
};

function EmailComp() {
  const [mailboxes, setMailboxes] = useState(() => ({
    INBOX: [],
    sent: readStoredPendingSentEmails(),
  }));
  const [remoteSentEmails, setRemoteSentEmails] = useState([]);
  const [sharedSentEmails, setSharedSentEmails] = useState([]);
  const [pendingSentEmails, setPendingSentEmails] = useState(() => readStoredPendingSentEmails());
  const [loadedMailboxes, setLoadedMailboxes] = useState({
    INBOX: false,
    sent: false,
  });
  const [activeMailbox, setActiveMailbox] = useState('INBOX');
  const [emailLoading, setEmailLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedEmailLoading, setSelectedEmailLoading] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [composeModalOpen, setComposeModalOpen] = useState(false);
  const [composeSending, setComposeSending] = useState(false);
  const [composeForm, setComposeForm] = useState({
    to: '',
    cc: '',
    subject: '',
    message: '',
  });

  const fetchMailbox = useCallback(async (mailbox, options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setEmailLoading(true);
    }

    try {
      const endpoint = mailbox === 'sent' ? 'read-sent' : 'read-inbox';
      const response = await fetch(`${MAIL_API_BASE_URL}/${endpoint}?limit=30`);
      const data = await parseResponseJson(response);
      const nextEmails = Array.isArray(data.emails) ? data.emails : [];

      if (mailbox === 'sent') {
        setRemoteSentEmails(nextEmails);
      } else {
        setMailboxes((prev) => ({
          ...prev,
          [mailbox]: nextEmails
        }));
      }
      setLoadedMailboxes((prev) => ({
        ...prev,
        [mailbox]: true
      }));
    } catch (error) {
      console.error(`Failed to fetch ${mailbox}:`, error);
      if (!silent) {
        swal('Error', `Unable to load ${MAILBOX_LABELS[mailbox] || 'mailbox'}: ${error.message}`, 'error');
      }
    } finally {
      if (!silent) {
        setEmailLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchMailbox('INBOX');
  }, [fetchMailbox]);

  useEffect(() => {
    const sharedSentQuery = query(
      collection(db, 'SharedSentEmails'),
      orderBy('sentAt', 'desc'),
      firestoreLimit(80)
    );

    const unsubscribe = onSnapshot(sharedSentQuery, (snapshot) => {
      const nextSharedEmails = snapshot.docs.map((sharedDoc) => buildSharedSentEmail(sharedDoc.id, sharedDoc.data()));
      setSharedSentEmails(nextSharedEmails);
    }, (error) => {
      console.error('Failed to subscribe to shared sent emails:', error);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!loadedMailboxes[activeMailbox]) {
      fetchMailbox(activeMailbox);
    }
  }, [activeMailbox, fetchMailbox, loadedMailboxes]);

  useEffect(() => {
    const { mergedEmails, remainingPendingEmails } = mergeSentEmails(
      remoteSentEmails,
      sharedSentEmails,
      pendingSentEmails
    );

    setMailboxes((prev) => (
      areEmailListsEqual(prev.sent, mergedEmails)
        ? prev
        : { ...prev, sent: mergedEmails }
    ));

    if (!areEmailListsEqual(pendingSentEmails, remainingPendingEmails)) {
      setPendingSentEmails(remainingPendingEmails);
    }
  }, [pendingSentEmails, remoteSentEmails, sharedSentEmails]);

  const emails = mailboxes[activeMailbox] || [];
  const unreadEmails = useMemo(
    () => (mailboxes.INBOX || []).filter((email) => !email.seen).length,
    [mailboxes]
  );

  const addLocalSentEmail = useCallback((emailPayload) => {
    const localEmail = buildLocalSentEmail(emailPayload);

    setPendingSentEmails((prev) => [localEmail, ...prev]);
    setLoadedMailboxes((prev) => ({
      ...prev,
      sent: true
    }));

    return localEmail;
  }, []);

  useEffect(() => {
    storePendingSentEmails(pendingSentEmails);
  }, [pendingSentEmails]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const unreadInboxItems = (mailboxes.INBOX || []).filter((email) => !email.seen);
    const badgeState = {
      count: unreadInboxItems.length,
      newestUnreadId: String(unreadInboxItems[0]?.messageId || unreadInboxItems[0]?.uid || ''),
      hasFreshUnread: false,
      checkedAt: Date.now()
    };

    window.localStorage.setItem(LEFTBAR_EMAIL_BADGE_KEY, JSON.stringify(badgeState));
    window.dispatchEvent(new CustomEvent(LEFTBAR_EMAIL_BADGE_EVENT, { detail: badgeState }));
  }, [mailboxes]);

  const handleOpenEmail = async (emailSummary) => {
    if (emailSummary?.isLocalOnly || emailSummary?.isSharedCopy) {
      setSelectedEmail(emailSummary);
      setReplyMessage('');
      return;
    }

    setSelectedEmailLoading(true);
    setSelectedEmail(emailSummary);
    setReplyMessage('');

    try {
      const mailbox = emailSummary.mailbox || activeMailbox;
      const response = await fetch(`${MAIL_API_BASE_URL}/read-email/${emailSummary.uid}?mailbox=${encodeURIComponent(mailbox)}`);
      const data = await parseResponseJson(response);
      const email = data.email || emailSummary;
      setSelectedEmail(email);

      if (!emailSummary.seen && mailbox === 'INBOX') {
        fetch(`${MAIL_API_BASE_URL}/mark-seen/${emailSummary.uid}?mailbox=${encodeURIComponent(mailbox)}`, { method: 'POST' }).catch((error) => {
          console.error('Failed to mark email as seen:', error);
        });

        setMailboxes((prev) => ({
          ...prev,
          INBOX: prev.INBOX.map((item) => (
            item.uid === emailSummary.uid ? { ...item, seen: true } : item
          ))
        }));
      }
    } catch (error) {
      console.error('Error reading email:', error);
      swal('Error', `Unable to load this email: ${error.message}`, 'error');
    } finally {
      setSelectedEmailLoading(false);
    }
  };

  const sendEmail = async () => {
    if (!composeForm.to.trim() || !composeForm.subject.trim() || !composeForm.message.trim()) {
      swal('Missing Details', 'Add the recipient, subject, and message before sending.', 'error');
      return;
    }

    setComposeSending(true);

    try {
      const brandedHtml = buildCommunicationEmailHtml({
        subject: composeForm.subject,
        message: composeForm.message
      });

      const response = await fetch(`${MAIL_API_BASE_URL}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: composeForm.to,
          cc: composeForm.cc,
          subject: composeForm.subject,
          text: composeForm.message,
          html: brandedHtml
        })
      });

      const sendResult = await parseResponseJson(response);
      const localSentEmail = addLocalSentEmail({
        to: composeForm.to,
        cc: composeForm.cc,
        subject: composeForm.subject,
        text: composeForm.message,
        html: brandedHtml,
        messageId: sendResult.messageId
      });
      persistSharedSentEmail({
        to: composeForm.to,
        cc: composeForm.cc,
        subject: composeForm.subject,
        text: composeForm.message,
        html: brandedHtml,
        messageId: sendResult.messageId,
        references: []
      }).catch((error) => {
        console.error('Failed to save shared sent email:', error);
      });

      logAdminActivity({
        action: 'Send Email',
        details: `Sent email to ${composeForm.to}`,
        meta: { page: 'Emails', channel: 'Email', to: composeForm.to, subject: composeForm.subject }
      });

      setComposeModalOpen(false);
      setComposeForm({ to: '', cc: '', subject: '', message: '' });
      if (activeMailbox === 'sent') {
        setSelectedEmail(localSentEmail);
      }
      swal('Sent', 'Your email has been sent successfully.', 'success');
    } catch (error) {
      console.error('Error sending email:', error);
      swal('Error', `Unable to send email: ${error.message}`, 'error');
    } finally {
      setComposeSending(false);
    }
  };

  const sendReply = async () => {
    if (!selectedEmail?.uid) {
      swal('No Email Selected', 'Open an email first before replying.', 'error');
      return;
    }

    if (!replyMessage.trim()) {
      swal('Missing Reply', 'Write your reply before sending it.', 'error');
      return;
    }

    setReplySending(true);

    try {
      const brandedHtml = buildCommunicationEmailHtml({
        subject: ensureReplySubject(selectedEmail.subject),
        message: replyMessage
      });

      const response = await fetch(`${MAIL_API_BASE_URL}/reply-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: extractEmailAddress(selectedEmail.from),
          subject: ensureReplySubject(selectedEmail.subject),
          text: replyMessage,
          html: brandedHtml,
          inReplyTo: selectedEmail.messageId,
          references: selectedEmail.references || []
        })
      });

      const replyResult = await parseResponseJson(response);
      const localSentEmail = addLocalSentEmail({
        to: extractEmailAddress(selectedEmail.from),
        cc: '',
        subject: ensureReplySubject(selectedEmail.subject),
        text: replyMessage,
        html: brandedHtml,
        messageId: replyResult.messageId
      });
      persistSharedSentEmail({
        to: extractEmailAddress(selectedEmail.from),
        cc: '',
        subject: ensureReplySubject(selectedEmail.subject),
        text: replyMessage,
        html: brandedHtml,
        messageId: replyResult.messageId,
        inReplyTo: selectedEmail.messageId,
        references: selectedEmail.references || []
      }).catch((error) => {
        console.error('Failed to save shared replied email:', error);
      });

      logAdminActivity({
        action: 'Reply Email',
        details: `Replied to ${selectedEmail.from || 'email thread'}`,
        meta: { page: 'Emails', channel: 'Email', subject: selectedEmail.subject || '' }
      });

      setReplyMessage('');
      if (activeMailbox === 'sent') {
        setSelectedEmail(localSentEmail);
      }
      swal('Reply Sent', 'Your reply has been sent successfully.', 'success');
    } catch (error) {
      console.error('Error replying to email:', error);
      swal('Error', `Unable to send reply: ${error.message}`, 'error');
    } finally {
      setReplySending(false);
    }
  };

  return (
    <div className="clientsComp applicationsPage communicationsPage">
      <div className="appsHeader">
        <div>
          <p className="appsKicker">Communications</p>
          <h4>Email Workspace</h4>
          <p className="appsSubtext">Read your admissions inbox, send new emails, and reply to active threads from inside the platform.</p>
          <p className="appsSubtext">{`${ORGANIZATION_NAME} Admissions Inbox`}</p>
        </div>
        <div className="appsHeaderStats">
          <div className="appsStatChip">
            <span>Inbox</span>
            <strong>{mailboxes.INBOX.length}</strong>
          </div>
          <div className="appsStatChip">
            <span>Sent</span>
            <strong>{mailboxes.sent.length}</strong>
          </div>
          <div className="appsStatChip">
            <span>Unread</span>
            <strong>{unreadEmails}</strong>
          </div>
        </div>
      </div>

      <div className="appsFiltersCard communicationsSwitchCard">
        <div className="communicationsActionRow">
          <button
            className={`communicationsSwitchBtn ${activeMailbox === 'INBOX' ? 'communicationsSwitchBtn--active' : ''}`}
            onClick={() => {
              setActiveMailbox('INBOX');
              setSelectedEmail(null);
            }}
          >
            <FontAwesomeIcon icon={faInbox} />
            <span>Inbox</span>
          </button>
          <button
            className={`communicationsSwitchBtn ${activeMailbox === 'sent' ? 'communicationsSwitchBtn--active' : ''}`}
            onClick={() => {
              setActiveMailbox('sent');
              setSelectedEmail(null);
            }}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
            <span>Sent emails</span>
          </button>
          <button className="interviewScheduleBtn" onClick={() => fetchMailbox(activeMailbox)} disabled={emailLoading}>
            <FontAwesomeIcon icon={faRotateRight} />
            <span>{emailLoading ? 'Refreshing...' : `Refresh ${MAILBOX_LABELS[activeMailbox]}`}</span>
          </button>
          <button className="interviewCallBtn" onClick={() => setComposeModalOpen(true)}>
            <FontAwesomeIcon icon={faPaperPlane} />
            <span>Compose email</span>
          </button>
        </div>
      </div>

      <div className="communicationsEmailLayout">
        <div className="appsTableCard communicationsInboxPanel">
          <div className="appsTableHeader">
            <h6><FontAwesomeIcon icon={activeMailbox === 'sent' ? faPaperPlane : faInbox} /> {MAILBOX_LABELS[activeMailbox]}</h6>
            <span>{emails.length} messages</span>
          </div>
          <div className="communicationsInboxList">
            {emailLoading && emails.length === 0 ? (
              <div className="interviewsLoadingState">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : emails.length === 0 ? (
              <div className="appsEmptyState">No emails found in {MAILBOX_LABELS[activeMailbox].toLowerCase()}.</div>
            ) : (
              emails.map((email) => (
                (() => {
                  const sentStatus = activeMailbox === 'sent' ? getSentEmailStatus(email) : null;

                  return (
                    <button
                      key={email.uid}
                      className={`communicationsEmailItem ${selectedEmail?.uid === email.uid ? 'communicationsEmailItem--active' : ''}`}
                      onClick={() => handleOpenEmail(email)}
                    >
                      <div className="communicationsEmailItemTop">
                        <strong>{activeMailbox === 'sent' ? (email.to || 'Unknown recipient') : (email.from || 'Unknown sender')}</strong>
                        <span>{formatDateTime(email.date)}</span>
                      </div>
                      <p>{email.subject || '(No subject)'}</p>
                      <small>{email.text || 'Open email to read more.'}</small>
                      {activeMailbox === 'INBOX' && !email.seen && <span className="communicationsUnreadDot">Unread</span>}
                      {sentStatus ? (
                        <span className={`communicationsEmailStatus communicationsEmailStatus--${sentStatus.tone}`}>
                          {sentStatus.label}
                        </span>
                      ) : null}
                    </button>
                  );
                })()
              ))
            )}
          </div>
        </div>

        <div className="appsTableCard communicationsReaderPanel">
          <div className="appsTableHeader">
            <h6><FontAwesomeIcon icon={faEnvelope} /> Email Viewer</h6>
            <span>{selectedEmail?.uid ? `UID ${selectedEmail.uid}` : 'Select a message'}</span>
          </div>

          {selectedEmailLoading ? (
            <div className="interviewsLoadingState">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : selectedEmail ? (
            <div className="communicationsReaderContent">
              <div className="communicationsReaderMeta">
                <h5>{selectedEmail.subject || '(No subject)'}</h5>
                <p><strong>From:</strong> {selectedEmail.from || 'Unknown sender'}</p>
                <p><strong>To:</strong> {selectedEmail.to || '—'}</p>
                {selectedEmail.cc ? <p><strong>CC:</strong> {selectedEmail.cc}</p> : null}
                <p><strong>Date:</strong> {formatDateTime(selectedEmail.date)}</p>
                {activeMailbox === 'sent' ? (
                  <p>
                    <strong>Status:</strong>{' '}
                    <span className={`communicationsEmailStatus communicationsEmailStatus--${getSentEmailStatus(selectedEmail).tone}`}>
                      {getSentEmailStatus(selectedEmail).label}
                    </span>
                  </p>
                ) : null}
              </div>

              <div className="communicationsReaderBody">
                {selectedEmail.html ? (
                  <div dangerouslySetInnerHTML={{ __html: selectedEmail.html }} />
                ) : (
                  <pre>{selectedEmail.text || 'No email body available.'}</pre>
                )}
              </div>

              {activeMailbox === 'INBOX' && (
                <div className="communicationsReplyBox">
                  <div className="communicationsReplyHeader">
                    <h6><FontAwesomeIcon icon={faReply} /> Reply</h6>
                    <span>{ensureReplySubject(selectedEmail.subject)}</span>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    placeholder="Write your reply here..."
                    value={replyMessage}
                    onChange={(event) => setReplyMessage(event.target.value)}
                  />
                  <div className="communicationsReplyActions">
                    <Button variant="dark" onClick={sendReply} disabled={replySending}>
                      {replySending ? 'Sending reply...' : 'Send reply'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="appsEmptyState communicationsReaderEmpty">
              Select an email from the inbox to read and reply.
            </div>
          )}
        </div>
      </div>

      <Modal show={composeModalOpen} onHide={() => setComposeModalOpen(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Compose Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>To</Form.Label>
            <Form.Control
              type="text"
              placeholder="recipient@example.com"
              value={composeForm.to}
              onChange={(event) => setComposeForm((prev) => ({ ...prev, to: event.target.value }))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>CC</Form.Label>
            <Form.Control
              type="text"
              placeholder="Optional"
              value={composeForm.cc}
              onChange={(event) => setComposeForm((prev) => ({ ...prev, cc: event.target.value }))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Subject</Form.Label>
            <Form.Control
              type="text"
              placeholder="Email subject"
              value={composeForm.subject}
              onChange={(event) => setComposeForm((prev) => ({ ...prev, subject: event.target.value }))}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={9}
              placeholder="Write your email here..."
              value={composeForm.message}
              onChange={(event) => setComposeForm((prev) => ({ ...prev, message: event.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setComposeModalOpen(false)} disabled={composeSending}>
            Close
          </Button>
          <Button variant="dark" onClick={sendEmail} disabled={composeSending}>
            {composeSending ? 'Sending...' : 'Send email'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default EmailComp;
